import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  BadgeDollarSign,
  Loader2,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { WebsitePlanModal } from './WebsitePlanModal';
import {
  useBulkDeletePlans,
  useDeletePlan,
  useUpdatePlan,
  useWebsitePlans,
} from '../../services/httpServices/queries';
import type { WebsitePlan } from '../../services/httpServices/websiteService';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Manages the pricing plans shown in the home page "Plans & Pricing" section.
 * Mirrors the testimonials screen so admins get the same search, filter, bulk
 * delete and inline publish/hide workflow.
 */
export function WebsitePlans() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WebsitePlan | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebsitePlan | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, error } = useWebsitePlans({
    page,
    limit: 20,
    search: debouncedSearch,
    status,
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const updateMutation = useUpdatePlan();
  const deleteMutation = useDeletePlan();
  const bulkDeleteMutation = useBulkDeletePlans();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [status]);

  useEffect(() => {
    const visible = new Set(items.map((item) => item.id));
    setSelected((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [items]);

  const allSelected = items.length > 0 && items.every((item) => selected.includes(item.id));
  const someSelected = items.some((item) => selected.includes(item.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelected(allSelected ? [] : items.map((item) => item.id));
  };

  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (plan: WebsitePlan) => {
    setEditing(plan);
    setModalOpen(true);
  };

  const handleToggleActive = async (plan: WebsitePlan) => {
    try {
      await updateMutation.mutateAsync({ id: plan.id, data: { isActive: !plan.isActive } });
    } catch {
      toast.error('Failed to update plan');
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Plan deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete plan');
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      toast.success(`Deleted ${ids.length} plan${ids.length === 1 ? '' : 's'}`);
      setSelected([]);
      setBulkConfirmOpen(false);
    } catch {
      toast.error('Failed to delete selected plans');
    }
  };

  const hasFilters = !!debouncedSearch || !!status;
  const isInitialLoading = isLoading && items.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <BadgeDollarSign className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Plans</h1>
            <p className="text-sm text-text-muted">
              Manage the pricing plans shown in the home page "Plans & Pricing" section.
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add plan
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name, price or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-48"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selected.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelected([])}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && items.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load plans. Please try again.</span>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-surface rounded-xl border border-dashed border-border py-16 text-center">
          <BadgeDollarSign className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-text font-medium">
            {hasFilters
              ? 'No plans match your filters.'
              : 'No plans yet. Add your first plan to get started.'}
          </p>
          {hasFilters ? (
            <button
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
              className="mt-2 text-sm text-primary hover:underline"
            >
              Clear filters
            </button>
          ) : (
            <button
              onClick={openCreate}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Add plan
            </button>
          )}
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={items.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Price
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Features
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((plan) => (
                  <tr key={plan.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(plan.id)}
                        onChange={() => toggleOne(plan.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(plan)}
                          className="text-sm font-medium text-text hover:text-primary text-left"
                        >
                          {plan.name}
                        </button>
                        {plan.popular && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[0.6875rem] font-medium text-primary">
                            <Star size={10} fill="currentColor" /> Popular
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p className="text-xs text-text-muted truncate max-w-[320px]">
                          {plan.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-primary">{plan.price}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-muted">
                      {plan.features?.length ?? 0} included
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-text-muted">
                      {plan.displayOrder}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          plan.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggleActive(plan)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title={plan.isActive ? 'Hide from website' : 'Show on website'}
                        >
                          {plan.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button
                          onClick={() => openEdit(plan)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(plan)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={20}
            onPageChange={setPage}
            itemName="plans"
          />
        </div>
      )}

      <WebsitePlanModal isOpen={modalOpen} plan={editing} onClose={() => setModalOpen(false)} />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete plan?"
        description={
          <>
            This will delete the <strong className="text-text">{deleteTarget?.name}</strong> plan
            from the website. This cannot be undone.
          </>
        }
        confirmLabel="Delete plan"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selected.length} plan${selected.length === 1 ? '' : 's'}?`}
        description="This removes the selected plans from the home page. This cannot be undone."
        confirmLabel={`Delete ${selected.length} plan${selected.length === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
